import type { ReactElement } from 'react';
import { useState, useCallback, useMemo } from 'react';
import { NeonButton, NeonButtonFooter, NeonModalDialog, NeonRadioField } from '@ps-refarch-ux/neon';
import type { RadioOption } from '@ps-refarch-ux/neon';
import { NeonButtonTypes } from '@/constants/neon-constants';
import {
  CREDIT_RECOVERY_LABEL,
  CREDIT_RECOVERY_DESCRIPTION,
  CREDIT_RECOVERY_BULLETS,
  CREDIT_RECOVERY_OPTIONS,
  GRADE_CREDIT_ALERTS_LABEL,
  GRADE_CREDIT_ALERTS_DESCRIPTION,
  GRADE_CREDIT_ALERTS_NOTE,
  GRADE_CREDIT_ALERTS_BUTTON,
  GRADE_CREDIT_ALERTS_EDIT_BUTTON,
  GRADE_CREDIT_ALERTS_REMOVE_BUTTON,
  GRADE_CREDIT_ALERTS_PANEL_COL_GRADE,
  GRADE_CREDIT_ALERTS_PANEL_COL_MIN,
  GRADE_CREDIT_ALERTS_PANEL_COL_MAX,
  CONFIGURATIONS_ID,
  REMOVE_CREDIT_ALERTS_MODAL_TITLE,
  REMOVE_CREDIT_ALERTS_MODAL_BODY_1,
  REMOVE_CREDIT_ALERTS_MODAL_BODY_2,
  REMOVE_CREDIT_ALERTS_MODAL_CANCEL,
  REMOVE_CREDIT_ALERTS_MODAL_CONFIRM,
  CREDIT_DEFICIENCY_LABEL,
  CREDIT_DEFICIENCY_DESCRIPTION,
  CREDIT_DEFICIENCY_NOTE,
  CREDIT_DEFICIENCY_NOTE_BOLD,
  CREDIT_DEFICIENCY_NOTE_SUFFIX,
  CREDIT_DEFICIENCY_BUTTON,
  CREDIT_DEFICIENCY_EDIT_BUTTON,
  CREDIT_DEFICIENCY_REMOVE_BUTTON,
  CREDIT_DEFICIENCY_PANEL_COL_GRADE,
  CREDIT_DEFICIENCY_PANEL_COL_MIN_CUMULATIVE,
  REMOVE_CREDIT_DEFICIENCY_MODAL_TITLE,
  REMOVE_CREDIT_DEFICIENCY_MODAL_BODY,
  REMOVE_CREDIT_DEFICIENCY_MODAL_BODY_BOLD,
  REMOVE_CREDIT_DEFICIENCY_MODAL_BODY_SUFFIX,
  REMOVE_CREDIT_DEFICIENCY_MODAL_CANCEL,
  REMOVE_CREDIT_DEFICIENCY_MODAL_CONFIRM,
} from '@/constants/configurations-constants';
import {
  bodyTextStyle,
  tabContentBlockStyle,
  sectionBlockStyle,
  sectionHeadingStyle,
  bulletListStyle,
  bulletListItemStyle,
  sectionTableWrapperStyle,
  sectionTableStyle,
  sectionTableHeaderStyle,
  sectionTableCellStyle,
  panelTableGradeColStyle,
  panelTableCreditsColStyle,
  panelTableDeficiencyMinColStyle,
  creditAlertsButtonRowStyle,
  modalBodyTextStyle,
  sectionDividerStyle,
} from './configurations.styles';
import type { GradeSetting, GradeDeficiency } from '@/repositories';
import { GradeCreditAlertsPanel } from './grade-credit-alerts-panel';
import { CreditDeficiencyPanel } from './credit-deficiency-panel';

const creditRecoveryOptions: RadioOption[] = CREDIT_RECOVERY_OPTIONS.map((o) => ({
  id: o.id,
  labelText: o.labelText,
  value: o.value,
}));

export interface AcademicRulesTabProps {
  creditRecovery: string;
  onCreditRecoveryChange: (value: string) => void;
  gradeSettings: GradeSetting[];
  onGradeSettingsChange: (settings: GradeSetting[]) => void;
  gradeDeficiency: GradeDeficiency[];
  onGradeDeficiencyChange: (items: GradeDeficiency[]) => void;
}

export function AcademicRulesTab({
  creditRecovery,
  onCreditRecoveryChange,
  gradeSettings,
  onGradeSettingsChange,
  gradeDeficiency,
  onGradeDeficiencyChange,
}: AcademicRulesTabProps): ReactElement {
  const [isCreditAlertsPanelOpen, setIsCreditAlertsPanelOpen] = useState(false);
  const [showRemoveCreditAlertsModal, setShowRemoveCreditAlertsModal] = useState(false);
  const [isCreditDeficiencyPanelOpen, setIsCreditDeficiencyPanelOpen] = useState(false);
  const [showRemoveCreditDeficiencyModal, setShowRemoveCreditDeficiencyModal] = useState(false);

  const openCreditAlertsPanel = useCallback(() => setIsCreditAlertsPanelOpen(true), []);
  const closeCreditAlertsPanel = useCallback(() => setIsCreditAlertsPanelOpen(false), []);
  const handleCreditAlertsSave = useCallback(
    (settings: GradeSetting[]) => onGradeSettingsChange(settings),
    [onGradeSettingsChange]
  );
  const openRemoveCreditAlertsModal = useCallback(() => setShowRemoveCreditAlertsModal(true), []);
  const closeRemoveCreditAlertsModal = useCallback(() => setShowRemoveCreditAlertsModal(false), []);
  const confirmRemoveCreditAlerts = useCallback(() => {
    onGradeSettingsChange([]);
    setShowRemoveCreditAlertsModal(false);
  }, [onGradeSettingsChange]);

  const hasGradeSettings = gradeSettings.length > 0;
  const sortedGradeSettings = useMemo(
    () => [...gradeSettings].sort((a, b) => a.grade - b.grade),
    [gradeSettings]
  );

  const openCreditDeficiencyPanel = useCallback(() => setIsCreditDeficiencyPanelOpen(true), []);
  const closeCreditDeficiencyPanel = useCallback(() => setIsCreditDeficiencyPanelOpen(false), []);
  const handleCreditDeficiencySave = useCallback(
    (items: GradeDeficiency[]) => onGradeDeficiencyChange(items),
    [onGradeDeficiencyChange]
  );
  const openRemoveCreditDeficiencyModal = useCallback(
    () => setShowRemoveCreditDeficiencyModal(true),
    []
  );
  const closeRemoveCreditDeficiencyModal = useCallback(
    () => setShowRemoveCreditDeficiencyModal(false),
    []
  );
  const confirmRemoveCreditDeficiency = useCallback(() => {
    onGradeDeficiencyChange([]);
    setShowRemoveCreditDeficiencyModal(false);
  }, [onGradeDeficiencyChange]);

  const hasGradeDeficiency = gradeDeficiency.length > 0;
  const sortedGradeDeficiency = useMemo(
    () => [...gradeDeficiency].sort((a, b) => a.grade - b.grade),
    [gradeDeficiency]
  );

  return (
    <div id="academic-rules-content" style={tabContentBlockStyle} data-testid="academic-rules-tab">
      <section style={sectionBlockStyle} aria-labelledby="heading-credit-recovery">
        <h2 id="heading-credit-recovery" style={sectionHeadingStyle}>
          {CREDIT_RECOVERY_LABEL}
        </h2>
        <p style={bodyTextStyle}>{CREDIT_RECOVERY_DESCRIPTION}</p>
        <ul style={bulletListStyle} aria-label="Credit recovery details">
          {CREDIT_RECOVERY_BULLETS.map((text, i) => (
            <li key={i} style={bulletListItemStyle}>
              {text}
            </li>
          ))}
        </ul>
        <NeonRadioField
          id="credit-recovery-field"
          dataLabelText={CREDIT_RECOVERY_LABEL}
          dataShowLabel={false}
          modelValue={creditRecovery}
          modelValueChange={(value) => value !== undefined && onCreditRecoveryChange(value)}
          options={creditRecoveryOptions}
          dataControlLayout="column"
        />
      </section>

      <hr style={sectionDividerStyle} />

      <section style={sectionBlockStyle} aria-labelledby="heading-grade-credit-alerts">
        <h2 id="heading-grade-credit-alerts" style={sectionHeadingStyle}>
          {GRADE_CREDIT_ALERTS_LABEL}
        </h2>
        <p style={bodyTextStyle}>{GRADE_CREDIT_ALERTS_DESCRIPTION}</p>
        <p style={bodyTextStyle}>{GRADE_CREDIT_ALERTS_NOTE}</p>
        {!hasGradeSettings ? (
          <NeonButton
            id="set-up-credit-alerts"
            dataType={NeonButtonTypes.Secondary}
            dataText={GRADE_CREDIT_ALERTS_BUTTON}
            onClick={openCreditAlertsPanel}
          />
        ) : (
          <>
            <div style={sectionTableWrapperStyle}>
              <table style={sectionTableStyle}>
                <thead>
                  <tr>
                    <th style={{ ...sectionTableHeaderStyle, ...panelTableGradeColStyle }}>
                      {GRADE_CREDIT_ALERTS_PANEL_COL_GRADE}
                    </th>
                    <th style={{ ...sectionTableHeaderStyle, ...panelTableCreditsColStyle }}>
                      {GRADE_CREDIT_ALERTS_PANEL_COL_MIN}
                    </th>
                    <th style={{ ...sectionTableHeaderStyle, ...panelTableCreditsColStyle }}>
                      {GRADE_CREDIT_ALERTS_PANEL_COL_MAX}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedGradeSettings.map((row) => (
                    <tr key={row.grade}>
                      <td style={{ ...sectionTableCellStyle, ...panelTableGradeColStyle }}>
                        {row.grade}
                      </td>
                      <td style={{ ...sectionTableCellStyle, ...panelTableCreditsColStyle }}>
                        {row.minCredits ?? '—'}
                      </td>
                      <td style={{ ...sectionTableCellStyle, ...panelTableCreditsColStyle }}>
                        {row.maxCredits ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={creditAlertsButtonRowStyle} data-testid="credit-alerts-actions">
              <NeonButton
                id="edit-credit-alerts"
                dataType={NeonButtonTypes.Secondary}
                dataText={GRADE_CREDIT_ALERTS_EDIT_BUTTON}
                onClick={openCreditAlertsPanel}
              />
              <NeonButton
                id="remove-credit-alerts"
                dataType={NeonButtonTypes.Secondary}
                dataText={GRADE_CREDIT_ALERTS_REMOVE_BUTTON}
                onClick={openRemoveCreditAlertsModal}
              />
            </div>
          </>
        )}
      </section>

      <GradeCreditAlertsPanel
        isOpen={isCreditAlertsPanelOpen}
        onClose={closeCreditAlertsPanel}
        initialGradeSettings={hasGradeSettings ? gradeSettings : undefined}
        onSave={handleCreditAlertsSave}
      />

      {showRemoveCreditAlertsModal && (
        <NeonModalDialog
          id={CONFIGURATIONS_ID.RemoveCreditAlertsModal}
          dataSize="small"
          dataShowCloseButton
          neonDialogHasClosed={closeRemoveCreditAlertsModal}
        >
          <div data-slot="dialog-header-title">{REMOVE_CREDIT_ALERTS_MODAL_TITLE}</div>
          <div data-slot="dialog-body">
            <div style={modalBodyTextStyle}>{REMOVE_CREDIT_ALERTS_MODAL_BODY_1}</div>
            <div style={{ ...modalBodyTextStyle, marginTop: '16px' }}>
              {REMOVE_CREDIT_ALERTS_MODAL_BODY_2}
            </div>
          </div>
          <div data-slot="dialog-button-footer">
            <NeonButtonFooter
              id="remove-credit-alerts-modal-footer"
              buttons={[
                {
                  id: 'remove-credit-alerts-modal-cancel',
                  text: REMOVE_CREDIT_ALERTS_MODAL_CANCEL,
                  type: NeonButtonTypes.Borderless,
                  onClick: (): void => closeRemoveCreditAlertsModal(),
                },
              ]}
              primaryButton={{
                id: 'remove-credit-alerts-modal-confirm',
                text: REMOVE_CREDIT_ALERTS_MODAL_CONFIRM,
                type: NeonButtonTypes.Primary,
                onClick: (): void => confirmRemoveCreditAlerts(),
              }}
            />
          </div>
        </NeonModalDialog>
      )}

      <hr style={sectionDividerStyle} />

      <section style={sectionBlockStyle} aria-labelledby="heading-credit-deficiency">
        <h2 id="heading-credit-deficiency" style={sectionHeadingStyle}>
          {CREDIT_DEFICIENCY_LABEL}
        </h2>
        <p style={bodyTextStyle}>{CREDIT_DEFICIENCY_DESCRIPTION}</p>
        <p style={bodyTextStyle}>
          {CREDIT_DEFICIENCY_NOTE} <strong>{CREDIT_DEFICIENCY_NOTE_BOLD}</strong>
          {CREDIT_DEFICIENCY_NOTE_SUFFIX}
        </p>
        {!hasGradeDeficiency ? (
          <NeonButton
            id="set-up-credit-deficiency"
            dataType={NeonButtonTypes.Secondary}
            dataText={CREDIT_DEFICIENCY_BUTTON}
            onClick={openCreditDeficiencyPanel}
          />
        ) : (
          <>
            <div style={sectionTableWrapperStyle}>
              <table style={sectionTableStyle}>
                <thead>
                  <tr>
                    <th style={{ ...sectionTableHeaderStyle, ...panelTableGradeColStyle }}>
                      {CREDIT_DEFICIENCY_PANEL_COL_GRADE}
                    </th>
                    <th style={{ ...sectionTableHeaderStyle, ...panelTableDeficiencyMinColStyle }}>
                      {CREDIT_DEFICIENCY_PANEL_COL_MIN_CUMULATIVE}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedGradeDeficiency.map((row) => (
                    <tr key={row.grade}>
                      <td style={{ ...sectionTableCellStyle, ...panelTableGradeColStyle }}>
                        {row.grade}
                      </td>
                      <td style={{ ...sectionTableCellStyle, ...panelTableDeficiencyMinColStyle }}>
                        {row.minCredits ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={creditAlertsButtonRowStyle} data-testid="credit-deficiency-actions">
              <NeonButton
                id="edit-credit-deficiency"
                dataType={NeonButtonTypes.Secondary}
                dataText={CREDIT_DEFICIENCY_EDIT_BUTTON}
                onClick={openCreditDeficiencyPanel}
              />
              <NeonButton
                id="remove-credit-deficiency"
                dataType={NeonButtonTypes.Secondary}
                dataText={CREDIT_DEFICIENCY_REMOVE_BUTTON}
                onClick={openRemoveCreditDeficiencyModal}
              />
            </div>
          </>
        )}
      </section>

      <CreditDeficiencyPanel
        isOpen={isCreditDeficiencyPanelOpen}
        onClose={closeCreditDeficiencyPanel}
        initialGradeDeficiency={hasGradeDeficiency ? gradeDeficiency : undefined}
        onSave={handleCreditDeficiencySave}
      />

      {showRemoveCreditDeficiencyModal && (
        <NeonModalDialog
          id={CONFIGURATIONS_ID.RemoveCreditDeficiencyModal}
          dataSize="small"
          dataShowCloseButton
          neonDialogHasClosed={closeRemoveCreditDeficiencyModal}
        >
          <div data-slot="dialog-header-title">{REMOVE_CREDIT_DEFICIENCY_MODAL_TITLE}</div>
          <div data-slot="dialog-body">
            <div style={modalBodyTextStyle}>
              {REMOVE_CREDIT_DEFICIENCY_MODAL_BODY}{' '}
              <strong>{REMOVE_CREDIT_DEFICIENCY_MODAL_BODY_BOLD}</strong>
              {REMOVE_CREDIT_DEFICIENCY_MODAL_BODY_SUFFIX}
            </div>
          </div>
          <div data-slot="dialog-button-footer">
            <NeonButtonFooter
              id="remove-credit-deficiency-modal-footer"
              buttons={[
                {
                  id: 'remove-credit-deficiency-modal-cancel',
                  text: REMOVE_CREDIT_DEFICIENCY_MODAL_CANCEL,
                  type: NeonButtonTypes.Borderless,
                  onClick: (): void => closeRemoveCreditDeficiencyModal(),
                },
              ]}
              primaryButton={{
                id: 'remove-credit-deficiency-modal-confirm',
                text: REMOVE_CREDIT_DEFICIENCY_MODAL_CONFIRM,
                type: NeonButtonTypes.Primary,
                onClick: (): void => confirmRemoveCreditDeficiency(),
              }}
            />
          </div>
        </NeonModalDialog>
      )}
    </div>
  );
}
